/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useMemo, useState, useEffect } from 'react';
import { useWBL } from '../../context/WBLContext';
import { Briefcase, Award, TrendingUp, ChevronRight, Building2 } from 'lucide-react';
import { HeroBanner } from '../Common/HeroBanner';
import { db, isUsingMockKeys } from '../../firebase';
import { collection, onSnapshot } from 'firebase/firestore';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';

export const SuperadminDashboard: React.FC = () => {
  const {
    activeUser,
    activeTenant,
    users,
    applications,
    placements,
    interviews,
    opportunities,
    setCurrentView,
    selectedTerm
  } = useWBL();

  const [cardTab, setCardTab] = useState<'outlook' | 'education'>('outlook');
  const [foundations, setFoundations] = useState<any[]>([]);
  const [foundationProgress, setFoundationProgress] = useState<any[]>([]);

  useEffect(() => {
    if (isUsingMockKeys) return;
    const unsubF = onSnapshot(collection(db, 'foundations'), (snap) => {
      setFoundations(snap.docs.map(f => ({ id: f.id, ...(f.data() as any) })));
    });
    const unsubP = onSnapshot(collection(db, 'foundation_progress'), (snap) => {
      setFoundationProgress(snap.docs.map(p => ({ id: p.id, ...(p.data() as any) })));
    });
    return () => {
      unsubF();
      unsubP();
    };
  }, []);

  const allStudentUids = useMemo(() => (users || []).filter(u => u.role === 'student').map(s => s.uid), [users]);

  const studentApplications = useMemo(() => 
    applications.filter(a => (a.term === selectedTerm || !a.term)),
    [applications, selectedTerm]
  );
  
  const activePlacements = useMemo(() => 
    placements.filter(p => (p.term === selectedTerm || !p.term)),
    [placements, selectedTerm]
  );
  
  const upcomingInterviews = useMemo(() => 
    interviews,
    [interviews]
  );

  const appliedCount = studentApplications.length;
  const approvedCount = studentApplications.filter(a => ['accepted', 'approved', 'placed'].includes(a.status)).length;
  const declinedCount = studentApplications.filter(a => ['rejected', 'declined', 'withdrawn'].includes(a.status)).length;
  const interviewedCount = studentApplications.filter(a => ['reviewed', 'interviewing', 'scheduled'].includes(a.status) || a.interviewScheduled).length;

  const sectorStats = useMemo(() => {
    const tenantOpps = (opportunities || []).filter(o => o.status === 'open');
    const total = tenantOpps.length;

    if (total === 0) {
      return { total: 0, sectors: [] };
    }

    const counts: Record<string, number> = {};
    tenantOpps.forEach(opp => {
        const cluster = opp.careerCluster || opp.industry || 'General Technical';
        counts[cluster] = (counts[cluster] || 0) + 1;
    });

    const colors = ['#0094c8', '#10b981', '#f59e0b', '#8b5cf6', '#3b82f6', '#ec4899', '#f43f5e', '#84cc16'];
    const sectors = Object.entries(counts)
        .map(([name, count], index) => {
            const percentage = Math.round((count / total) * 100);
            const demand = percentage > 30 ? 'High Demand' : percentage > 15 ? 'Growing' : 'Stable';
            return { name, count, percentage, color: colors[index % colors.length], demand };
        })
        .sort((a, b) => b.count - a.count)
        .slice(0, 4);

    return { total, sectors };
  }, [opportunities]);

  const displayFoundations = useMemo(() => {
    if (foundations.length > 0) return foundations;
    return [
        { id: 'mock-f1', title: 'Workplace Communication', slides: [1, 2, 3, 4] },
        { id: 'mock-f2', title: 'Professional Etiquette', slides: [1, 2, 3, 4, 5] },
        { id: 'mock-f3', title: 'Problem Solving', slides: [1, 2, 3] },
        { id: 'mock-f4', title: 'Collaborative Teamwork', slides: [1, 2, 3, 4] }
    ];
  }, [foundations]);

  const displayProgress = useMemo(() => {
    return foundationProgress.filter(p => allStudentUids.includes(p.userId));
  }, [foundationProgress, allStudentUids]);

  const activeFoundationsCount = displayFoundations.length;
  const completedFoundationsCount = useMemo(() => {
    if (allStudentUids.length === 0) return 0;
    let totalCompleted = 0;
    allStudentUids.forEach(uid => {
      const completed = displayFoundations.filter(f => {
        const prog = displayProgress.find(p => p.foundationId === f.id && p.userId === uid);
        return prog?.completedAt || prog?.status === 'completed' || (prog?.quizScore && prog.quizScore >= 90);
      }).length;
      totalCompleted += completed;
    });
    return Math.round(totalCompleted / allStudentUids.length);
  }, [displayFoundations, displayProgress, allStudentUids]);

  const pendingFoundationsCount = Math.max(0, activeFoundationsCount - completedFoundationsCount);
  
  const chartData = [
    { name: 'Mastered', value: completedFoundationsCount },
    { name: 'Pending', value: pendingFoundationsCount > 0 ? pendingFoundationsCount : (completedFoundationsCount === 0 ? 4 : 0) }, 
  ];
  const COLORS = ['#0f172a', '#f3eee6'];
  const myPlacements = activePlacements;

  return (
    <div className="space-y-6 font-sans animate-fade-in">
      <HeroBanner 
        title="DASHBOARD"
        subtitle={`Scope: System-Wide Superadmin View`}
        pageKey="dashboard"
      />

      <div className="w-[95%] mx-auto space-y-8 pb-12">
        <div className={`grid grid-cols-1 ${activeTenant.foundationsEnabled !== false ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-8`}>
            {/* Application Pipeline */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200/70 shadow-[0_4px_20px_rgba(0,0,0,0.01)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.03)] hover:border-slate-300/80 transition-all duration-300 flex flex-col justify-between h-[420px] group">
                <div>
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-3">
                            <div style={{ backgroundColor: `${activeTenant.primaryColor}14`, color: activeTenant.primaryColor }} className="p-2.5 rounded-xl"><Briefcase className="w-5 h-5" /></div>
                            <div>
                                <h3 className="font-extrabold text-sm text-[#0f172a] tracking-tight uppercase">Application Pipeline</h3>
                                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">Current recruitment status</p>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3.5">
                        <div className="p-3.5 bg-blue-50/20 hover:bg-blue-50/40 border border-blue-100/50 rounded-xl transition-all duration-300 flex flex-col justify-between min-h-[82px] group/item">
                            <span className="text-[8.5px] font-black uppercase text-blue-500 tracking-wider">SUBMISSIONS</span>
                            <div className="flex justify-between items-baseline mt-2">
                                <p className="text-2xl font-black text-slate-800 tracking-tight">{appliedCount}</p>
                            </div>
                        </div>
                        <div className="p-3.5 bg-sky-50/20 hover:bg-sky-50/40 border border-sky-100/50 rounded-xl transition-all duration-300 flex flex-col justify-between min-h-[82px]">
                            <span className="text-[8.5px] font-black uppercase text-sky-600 tracking-wider">INTERVIEWING</span>
                            <div className="flex justify-between items-baseline mt-2">
                                <p className="text-2xl font-black text-sky-700 tracking-tight">{interviewedCount}</p>
                            </div>
                        </div>
                        <div className="p-3.5 bg-emerald-50/20 hover:bg-emerald-50/40 border border-emerald-100/50 rounded-xl transition-all duration-300 flex flex-col justify-between min-h-[82px]">
                            <span className="text-[8.5px] font-black uppercase text-emerald-600 tracking-wider">ACCEPTED</span>
                            <div className="flex justify-between items-baseline mt-2">
                                <p className="text-2xl font-black text-emerald-700 tracking-tight">{approvedCount}</p>
                            </div>
                        </div>
                        <div className="p-3.5 bg-rose-50/25 hover:bg-rose-50/45 border border-rose-100/50 rounded-xl transition-all duration-300 flex flex-col justify-between min-h-[82px]">
                            <span className="text-[8.5px] font-black uppercase text-rose-500 tracking-wider">DECLINED</span>
                            <div className="flex justify-between items-baseline mt-2">
                                <p className="text-2xl font-black text-rose-600 tracking-tight">{declinedCount}</p>
                            </div>
                        </div>
                    </div>

                    <div className={`mt-4 p-3 rounded-xl flex items-center justify-between border transition-all duration-300 ${
                        myPlacements.length > 0 
                            ? 'bg-emerald-500/5 border-emerald-500/10 hover:border-emerald-500/20' 
                            : 'bg-slate-50/50 border-slate-100'
                    }`}>
                        <div className="min-w-0 flex-1 pr-2">
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Active Internship</p>
                            <p className="text-[11px] font-extrabold text-[#0f172a] truncate uppercase">
                                {myPlacements[0] 
                                    ? `${myPlacements[0].opportunityTitle || 'Internship'} @ ${myPlacements[0].organizationName || 'Employer Worksite'}`
                                    : 'No Active Placements'}
                            </p>
                        </div>
                        <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase ${
                            myPlacements.length > 0 ? 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20' : 'bg-slate-100 text-slate-500 border border-slate-200/60'
                        } border`}>
                            {myPlacements.length > 0 ? 'ACTIVE' : 'SEARCHING'}
                        </span>
                    </div>
                </div>

                <button 
                    onClick={() => setCurrentView('applications')}
                    style={{ backgroundColor: `${activeTenant.primaryColor}14`, color: activeTenant.primaryColor, borderColor: activeTenant.primaryColor }}
                    className="w-full text-center text-[10px] font-black hover:text-white py-3 hover:shadow-md hover:-translate-y-0.5 rounded-xl transition-all duration-300 flex items-center justify-center space-x-1.5 uppercase tracking-widest mt-auto cursor-pointer border"
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = activeTenant.primaryColor}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = `${activeTenant.primaryColor}14`}
                >
                    VIEW SYSTEM PIPELINE
                </button>
            </div>

            {activeTenant.foundationsEnabled !== false && (
                /* Career Mastery */
                <div className="bg-white p-6 rounded-2xl border border-slate-200/70 shadow-[0_4px_20px_rgba(0,0,0,0.01)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.03)] hover:border-slate-300/80 transition-all duration-300 flex flex-col justify-between h-[420px] group">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-3">
                            <div style={{ backgroundColor: `${activeTenant.primaryColor}14`, color: activeTenant.primaryColor }} className="p-2.5 rounded-xl"><Award className="w-5 h-5" /></div>
                            <div>
                                <h3 className="font-extrabold text-sm text-[#0f172a] tracking-tight uppercase">Career Mastery</h3>
                                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">Soft-skill foundation progress</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-4 py-2">
                        <div className="w-[110px] h-[110px] relative shrink-0">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={chartData} innerRadius={36} outerRadius={48} paddingAngle={4} dataKey="value">
                                        {chartData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                <p className="text-xl font-black text-slate-800 tracking-tight leading-none">{completedFoundationsCount}</p>
                                <p className="text-[7.5px] uppercase font-black text-slate-400 tracking-wider mt-0.5">OF {activeFoundationsCount}</p>
                            </div>
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1.5">Mastery Rating</p>
                            <h4 className="text-base font-black text-[#0f172a] uppercase tracking-tight leading-none mb-1">
                                {completedFoundationsCount === activeFoundationsCount ? 'Distinguished' : completedFoundationsCount > 0 ? 'Proficient' : 'Beginning'}
                            </h4>
                            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-tight leading-normal">
                                {completedFoundationsCount} of {activeFoundationsCount} soft-skill courses mastered on average.
                            </p>
                        </div>
                    </div>

                    <div className="space-y-2 max-h-[140px] overflow-y-auto pr-1">
                        {displayFoundations.slice(0, 3).map((f) => {
                            return (
                                <div key={f.id} className="flex items-center justify-between text-xs bg-slate-50/60 hover:bg-slate-50 p-2.5 rounded-xl border border-slate-100 transition-all duration-200">
                                    <div className="flex items-center space-x-2 min-w-0 flex-1">
                                        <div className={`w-2 h-2 rounded-full shrink-0 bg-slate-300`} />
                                        <span className="font-extrabold text-[#0f172a] truncate uppercase tracking-tight text-[10px]">{f.title}</span>
                                    </div>
                                    <span className="text-[9px] font-mono font-bold text-slate-500 shrink-0 ml-2">
                                        --%
                                    </span>
                                </div>
                            );
                        })}
                    </div>

                    <button 
                        onClick={() => {
                            sessionStorage.setItem('superadminConsoleTab', 'CURRICULUM');
                            setCurrentView('console');
                        }}
                        style={{ backgroundColor: `${activeTenant.primaryColor}14`, color: activeTenant.primaryColor, borderColor: activeTenant.primaryColor }}
                        className="w-full text-center text-[10px] font-black hover:text-white py-3 hover:shadow-md hover:-translate-y-0.5 rounded-xl transition-all duration-300 flex items-center justify-center space-x-1.5 uppercase tracking-widest mt-auto cursor-pointer border"
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = activeTenant.primaryColor}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = `${activeTenant.primaryColor}14`}
                    >
                        ENTER FOUNDATION HUB
                    </button>
                </div>
            )}

            {/* My Education & Regional Outlook */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200/70 shadow-[0_4px_20px_rgba(0,0,0,0.01)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.03)] hover:border-slate-300/80 transition-all duration-300 flex flex-col justify-between h-[420px] group">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                        <div style={{ backgroundColor: `${activeTenant.primaryColor}14`, color: activeTenant.primaryColor }} className="p-2.5 rounded-xl"><TrendingUp className="w-5 h-5" /></div>
                        <div>
                            <h3 className="font-extrabold text-sm text-[#0f172a] tracking-tight uppercase">Regional Outlook</h3>
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">Available Jobs & Sectors</p>
                        </div>
                    </div>
                    <div className="flex p-0.5 bg-slate-100 rounded-lg shrink-0">
                        <button 
                            onClick={() => setCardTab('outlook')}
                            className={`px-3 py-1 text-[9px] font-black uppercase tracking-widest rounded-md transition-all ${cardTab === 'outlook' ? 'bg-white text-[#0f172a] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            Outlook
                        </button>
                        <button 
                            onClick={() => setCardTab('education')}
                            className={`px-3 py-1 text-[9px] font-black uppercase tracking-widest rounded-md transition-all ${cardTab === 'education' ? 'bg-white text-[#0f172a] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            Edu
                        </button>
                    </div>
                </div>

                {cardTab === 'outlook' ? (
                    <div className="space-y-4 animate-in fade-in duration-300">
                        <div className="flex items-center justify-between p-3 bg-slate-50/50 border border-slate-100 rounded-xl">
                            <div className="flex items-center space-x-2.5">
                                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Active Placement Pipeline</span>
                            </div>
                            <span className="text-xs font-black text-slate-800 bg-white px-2 py-0.5 rounded border border-slate-200">{sectorStats.total} Roles</span>
                        </div>
                        
                        {sectorStats.sectors.length === 0 ? (
                            <div className="flex flex-col items-center justify-center text-center py-12 space-y-2">
                                <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider">No Active Vacancies</span>
                                <span className="text-slate-300 text-[10px]">Active vacancies will automatically populate this list.</span>
                            </div>
                        ) : (
                            <div className="space-y-4 pt-1">
                                {sectorStats.sectors.map((sector, idx) => (
                                    <div key={idx} className="space-y-1.5">
                                        <div className="flex justify-between items-end text-xs">
                                            <span className="font-extrabold text-[#0f172a] uppercase tracking-tight text-[10px]">{sector.name}</span>
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-[9px] text-slate-400 font-bold tracking-wider">({sector.count} JOB{sector.count !== 1 ? 'S' : ''})</span>
                                                <span style={{ color: sector.color }} className="font-black text-[10px]">{sector.percentage}%</span>
                                            </div>
                                        </div>
                                        <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden flex">
                                            <div 
                                                className="h-full rounded-full transition-all duration-700" 
                                                style={{ width: `${sector.percentage}%`, backgroundColor: sector.color }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="space-y-4 animate-in fade-in duration-300 flex-1 flex flex-col justify-center py-4">
                        <div className="flex flex-col items-center justify-center text-center space-y-4 px-4">
                            <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-500 mb-2 border border-blue-100 shadow-sm">
                                <Building2 className="w-8 h-8" />
                            </div>
                            <div>
                                <h4 className="text-sm font-black text-slate-800 uppercase tracking-tight mb-1">Higher Education Links</h4>
                                <p className="text-[11px] text-slate-500 leading-relaxed font-medium">Explore university partnerships and articulation agreements connected to your regional career pathways.</p>
                            </div>
                        </div>
                    </div>
                )}

                <button 
                    onClick={() => setCurrentView('opportunities')}
                    className="w-full text-center text-[10px] font-black hover:text-[#0f172a] text-slate-500 py-3 hover:bg-slate-50 rounded-xl transition-all duration-300 flex items-center justify-center space-x-1.5 uppercase tracking-widest mt-auto border border-slate-200/60"
                >
                    <span>EXPLORE OPPORTUNITIES</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>
      </div>
    </div>
  );
};
